import * as React from "react";
import {
  Link,
  Outlet,
  useLocation,
  useMatches,
  useNavigate,
} from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import * as m from "~/paraglide/messages";
import { useSession, signOut, authClient } from "~/lib/auth-client";
import { clearSessionCache } from "~/lib/auth-guard";
import { ThemeToggle } from "~/lib/theme";
import { ErrorBoundary } from "~/components/ErrorBoundary";
import { LanguageSwitcher } from "~/components/LanguageSwitcher";
import { LowCreditBanner } from "~/components/LowCreditBanner";
import { CandidateAiNotice } from "~/components/CandidateAiNotice";
import { CommandPalette } from "~/components/CommandPalette";
import { orpc } from "~/lib/orpc-client";
import { useHumanReviewBackfill } from "~/lib/use-human-review-backfill";
import { icons } from "./SidebarIcons";
import { SidebarNav } from "./SidebarNav";
import { AlchemaLogo } from "~/components/AlchemaLogo";
import { SidebarCreditBalance } from "./SidebarCreditBalance";
import { LandingNav } from "./LandingNav";
import { A11yRouteAnnouncer } from "./A11yRouteAnnouncer";
import { TopBar } from "./TopBar";
import {
  getProtectedRouteLoginRedirect,
  isProtectedRoutePath,
} from "~/lib/protected-route-paths";
import { isPublicSeoLandingPath } from "~/lib/seo/public-landing-routes";


/* ── Low credit banner wrapper (fetches credit balance) ── */

function CreditBannerWrapper() {
  const creditQuery = useQuery(orpc.credits.getBalance.queryOptions());
  const data = creditQuery.data;

  if (!data) return null;

  return (
    <div className="px-4 sm:px-6 pt-3">
      <LowCreditBanner
        creditsRemaining={data.balance}
        tier={data.tier}
        resetAt={data.resetAt}
      />
    </div>
  );
}

/* ── App Shell: Sidebar + Top Bar + Content ── */

export function AppShell() {
  const { data: session, isPending: sessionLoading } = useSession();
  useHumanReviewBackfill();
  const navigate = useNavigate();
  const location = useLocation();
  const matches = useMatches();
  const matchKey = matches[matches.length - 1]?.id || "root";
  const lastPath = location.pathname || "/";
  const isLanding = lastPath === "/";
  const isAuthPage = lastPath.startsWith("/auth");
  const isPublicPage = isLanding || lastPath === "/pricing" || lastPath === "/privacy" || lastPath === "/about" || lastPath === "/vpat" || isPublicSeoLandingPath(lastPath) || lastPath.startsWith("/gehalt") || lastPath.startsWith("/skills") || lastPath.startsWith("/blog") || lastPath.startsWith("/uni") || lastPath === "/impressum" || lastPath === "/agb" || lastPath === "/barrierefreiheit" || lastPath === "/widerruf" || lastPath === "/kuendigung";
  const isAppPage = !isPublicPage && !isAuthPage;
  const protectedLoginRedirect = isProtectedRoutePath(lastPath)
    ? getProtectedRouteLoginRedirect(lastPath)
    : null;

  // Track if we've ever seen an authenticated session to avoid flashing landing page
  const hadSession = React.useRef(false);
  if (session?.user) hadSession.current = true;

  const authPageRedirectAttempt = React.useRef<string | null>(null);
  const showSidebar = (session?.user || (sessionLoading && hadSession.current)) && isAppPage;

  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [cmdPaletteOpen, setCmdPaletteOpen] = React.useState(false);
  const menuButtonRef = React.useRef<HTMLButtonElement>(null);
  const sidebarRef = React.useRef<HTMLElement>(null);

  // ⌘K / Ctrl+K to toggle command palette
  React.useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setCmdPaletteOpen((prev) => !prev);
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  // When sidebar opens on mobile, focus the first nav link
  React.useEffect(() => {
    if (sidebarOpen) {
      requestAnimationFrame(() => {
        const firstLink = sidebarRef.current?.querySelector<HTMLAnchorElement>("a.nav-item");
        firstLink?.focus();
      });
    }
  }, [sidebarOpen]);

  // Focus trap: keep Tab cycling within sidebar when open on mobile
  React.useEffect(() => {
    if (!sidebarOpen) return;
    function handleTab(e: KeyboardEvent) {
      if (e.key !== "Tab" || !sidebarRef.current) return;
      const focusable = sidebarRef.current.querySelectorAll<HTMLElement>(
        'a, button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener("keydown", handleTab);
    return () => document.removeEventListener("keydown", handleTab);
  }, [sidebarOpen]);

  const closeSidebar = React.useCallback(() => {
    setSidebarOpen(false);
    menuButtonRef.current?.focus();
  }, []);

  // Authenticated user on landing/auth pages -> redirect to dashboard (SPA navigation).
  // Server-confirm and make the redirect one-shot per route/user so a stale
  // hydrated client session cannot churn between /auth/login and /dashboard.
  React.useEffect(() => {
    if (sessionLoading || !session?.user || (!isLanding && !isAuthPage)) return;

    const userKey = session.user.id || session.user.email || "authenticated";
    const redirectKey = `${lastPath}:${userKey}`;
    if (authPageRedirectAttempt.current === redirectKey) return;

    let cancelled = false;
    authPageRedirectAttempt.current = redirectKey;

    authClient.getSession()
      .then(({ data }) => {
        if (cancelled) return;

        if (data?.session || data?.user) {
          navigate({ to: "/dashboard", replace: true });
          return;
        }

        clearSessionCache();
      })
      .catch(() => {
        if (!cancelled) {
          clearSessionCache();
        }
      });

    return () => {
      cancelled = true;
    };
  }, [session?.user, sessionLoading, isLanding, isAuthPage, lastPath, navigate]);

  // Unauthenticated protected app routes should never fall back to the public shell.
  React.useEffect(() => {
    if (!sessionLoading && !session?.user && protectedLoginRedirect) {
      navigate({ to: protectedLoginRedirect, replace: true });
    }
  }, [navigate, protectedLoginRedirect, session?.user, sessionLoading]);

  if (!sessionLoading && !session?.user && protectedLoginRedirect) {
    return (
      <div className="flex min-h-screen items-center justify-center" role="status" aria-live="polite" aria-label="Redirecting to sign in" style={{ background: "var(--bg)" }}>
        <div className="animate-fade-in text-sm" style={{ color: "var(--text-muted)" }}>
          {m.common_loading()}
        </div>
      </div>
    );
  }

  // Only show loading on first visit to app page (before we know if user is logged in)
  if (sessionLoading && isAppPage && !hadSession.current) {
    return (
      <div className="flex min-h-screen items-center justify-center" aria-busy="true" style={{ background: "var(--bg)" }}>
        <A11yRouteAnnouncer />
        <div className="animate-fade-in text-sm" role="status" aria-live="polite" style={{ color: "var(--text-muted)" }}>{m.common_loading()}</div>
      </div>
    );
  }

  // While session is loading on app pages, show spinner to avoid LandingNav flash
  if (sessionLoading && isAppPage) {
    return (
      <div className="flex min-h-screen items-center justify-center" role="status" aria-label="Loading" style={{ background: "var(--bg)" }}>
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-current border-t-transparent" style={{ color: "var(--text-muted)" }} />
      </div>
    );
  }

  if (!showSidebar) {
    return (
      <>
        <A11yRouteAnnouncer />
        <LandingNav session={session} />
        <main id="main-content" className={`animate-fade-in ${isLanding ? "" : "pt-20"}`}>
          <ErrorBoundary>
            <div key={matchKey} className="animate-page-enter">
              <Outlet />
            </div>
          </ErrorBoundary>
        </main>
      </>
    );
  }

  return (
    <div className="app-layout">
      <A11yRouteAnnouncer />
      <CommandPalette isOpen={cmdPaletteOpen} onClose={() => setCmdPaletteOpen(false)} />
      {/* Sidebar overlay (mobile) */}
      { }
      <div
        role="presentation"
        className={`app-sidebar-overlay ${sidebarOpen ? "visible" : ""}`}
        onClick={closeSidebar}
      />

      {/* Sidebar */}
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- Escape key to close sidebar */}
      <aside
        ref={sidebarRef}
        className={`app-sidebar ${sidebarOpen ? "open" : ""}`}
        aria-label="Main navigation"
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            closeSidebar();
          }
        }}
      >
        <div
          className="flex items-center gap-2 px-4 border-b"
          style={{
            height: "var(--topbar-height, 44px)",
            borderColor: "var(--border-subtle)",
          }}
        >
          <Link
            to="/dashboard"
            className="flex items-center min-w-0 no-underline"
            aria-label="Alchema dashboard"
          >
            <AlchemaLogo style={{ fontSize: "1.35rem" }} />
          </Link>
          <button
            type="button"
            className="ml-auto text-[11px] px-1.5 py-0.5 rounded cursor-pointer"
            style={{
              background: "var(--surface-raised)",
              color: "var(--text-muted)",
              fontFamily: "var(--font-mono)",
              border: "none",
            }}
            onClick={() => setCmdPaletteOpen(true)}
            aria-label="Open command palette"
          >
            ⌘K
          </button>
        </div>

        <SidebarNav onLinkClick={() => setSidebarOpen(false)} />

        <div
          className="border-t px-2 py-2 flex flex-col gap-0.5"
          style={{ borderColor: "var(--border-subtle)" }}
        >
          <Link
            to="/pricing"
            className="nav-item sm:hidden"
            onClick={() => setSidebarOpen(false)}
          >
            <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="1" x2="12" y2="23" />
              <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
            </svg>
            <span>{m.nav_pricing()}</span>
          </Link>
          <div className="md:hidden px-2 py-1">
            <LanguageSwitcher />
          </div>
          <div className="flex items-center gap-2 px-2 py-1 sm:hidden">
            <ThemeToggle />
          </div>

          <SidebarCreditBalance />

          <Link
            to="/settings"
            className="nav-item"
            activeProps={{ className: "nav-item active" }}
            onClick={() => setSidebarOpen(false)}
          >
            {icons.settings}
            <span>{m.nav_settings()}</span>
          </Link>

          <div className="flex items-center gap-2 px-2 py-1.5 mt-1 border-t" style={{ borderColor: "var(--border-subtle)" }}>
            <div
              className="flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-medium"
              style={{ background: "var(--accent-subtle)", color: "var(--accent-text)" }}
            >
              {session?.user?.name?.charAt(0)?.toUpperCase() || "U"}
            </div>
            <span className="text-[12px] font-medium truncate flex-1" style={{ color: "var(--text-secondary)" }}>
              {session?.user?.name}
            </span>
            <button
              onClick={() => {
                clearSessionCache();
                void signOut().then(() => navigate({ to: "/", replace: true }));
              }}
              className="text-[11px] transition-colors"
              style={{ color: "var(--text-muted)" }}
            >
              {m.nav_sign_out()}
            </button>
          </div>
        </div>
      </aside>

      {/* Main content area -- inert when mobile sidebar open to trap focus */}
      {/* @ts-expect-error -- inert is a valid HTML attribute */}
      <div className="app-main flex flex-col" inert={sidebarOpen ? "" : undefined}>
        <TopBar
          session={session}
          onMenuToggle={() => setSidebarOpen(!sidebarOpen)}
          sidebarOpen={sidebarOpen}
          onCmdPaletteOpen={() => setCmdPaletteOpen(true)}
        />

        {/* Low credit warning banner */}
        <CreditBannerWrapper />

        {/* EU AI Act Art. 50 candidate AI-interaction disclosure + human-review toggle */}
        <div className="px-4 sm:px-6 pt-3">
          <CandidateAiNotice />
        </div>

        {/* Page content -- full-bleed for editor routes, padded for everything else */}
        {/* CSS-only transitions for authenticated routes -- avoids framer-motion in critical path */}
        <main
          id="main-content"
          className={`flex-1 ${/^\/resumes\/[^/]+/.test(lastPath) ? "" : "px-4 sm:px-6 lg:px-8 py-6"}`}
        >
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
}
